import React, { useState } from 'react';
import { companyInfo } from '../../data/mock';
import { Phone, Mail, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: response.data.message
        });
        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          message: ''
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Une erreur s\'est produite. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section id="contact" className="py-8 sm:py-12 lg:py-16 xl:py-20 bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-2xl sm:max-w-4xl lg:max-w-6xl xl:max-w-7xl mx-auto">
          {/* Header responsive */}
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Nous contacter
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-sm sm:max-w-2xl lg:max-w-3xl mx-auto px-4">
              Prêt à découvrir nos services ? Notre équipe est à votre disposition 
              pour répondre à toutes vos questions et vous accompagner dans votre projet.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 xl:gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Parlons de votre projet
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Que vous souhaitiez mettre en place une solution de restauration d'entreprise, 
                organiser un événement ou bénéficier de nos services de consulting, 
                nous sommes là pour vous accompagner.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-[#4b9fcc]/5 transition-colors duration-300">
                <div className="w-12 h-12 bg-[#4b9fcc]/10 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#4b9fcc]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Téléphone</h4>
                  <p className="text-gray-600">{companyInfo.phone}</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-[#4b9fcc]/5 transition-colors duration-300">
                <div className="w-12 h-12 bg-[#4b9fcc]/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#4b9fcc]" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Email</h4>
                  <p className="text-gray-600">{companyInfo.email}</p>
                </div>
              </div>
            </div>

            {/* Section LinkedIn */}
            <div className="mt-8 space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Linkedin className="w-5 h-5 text-[#0077B5] mr-2" />
                  Nous suivre sur LinkedIn
                </h3>
              </div>

              {/* LinkedIn Entreprise */}
              <div className="bg-gradient-to-br from-[#0077B5]/5 to-[#0077B5]/10 rounded-xl p-4 border border-[#0077B5]/20">
                <a 
                  href="https://www.linkedin.com/company/sisens" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 hover:scale-[1.02] transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-[#0077B5]/20 rounded-xl flex items-center justify-center">
                    <Linkedin className="w-6 h-6 text-[#0077B5]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">SISENS</h4>
                    <p className="text-sm text-gray-600">Page entreprise officielle</p>
                  </div>
                  <div className="text-[#0077B5]">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </a>
              </div>

              {/* Note: LinkedIn des dirigeants déplacé dans la section About sous leurs photos */}
            </div>
          </div>

          {/* Contact Form Placeholder */}
          <div className="bg-gradient-to-br from-[#4b9fcc]/5 to-[#e71f74]/5 rounded-xl p-6 md:p-8 shadow-sm border border-[#4b9fcc]/10">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Nous contacter
            </h3>
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              {submitStatus.message && (
                <div className={`p-4 rounded-xl text-sm font-medium ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    minLength={2}
                    maxLength={100}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4b9fcc] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    maxLength={100}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4b9fcc] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Nom de votre entreprise"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4b9fcc] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="votre@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  minLength={10}
                  maxLength={2000}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4b9fcc] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Décrivez-nous votre projet..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.email || !formData.message}
                className="w-full bg-[#4b9fcc] text-white py-4 rounded-xl hover:bg-[#e71f74] transition-all duration-300 transform hover:scale-105 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-[#4b9fcc]"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
              </button>
            </form>
            
            {/* Notice RGPD */}
            <div className="mt-4 text-xs text-gray-500 leading-relaxed">
              Les informations recueillies à partir de ce formulaire sont enregistrées par SISENS pour répondre à votre demande. Pour en savoir plus, consultez notre{' '}
              <Link 
                to="/politique-confidentialite" 
                className="text-[#4b9fcc] hover:text-[#e71f74] underline transition-colors duration-300"
              >
                Politique de confidentialité
              </Link>.
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;